// 15-keyframes.ts
import type { DocPageContent } from '../docs-content'

const page = {
  order: 15,
  slug: 'keyframes',
  title: 'Animación con Fotogramas Clave',
  description:
    'Anima valores de transformación, recorte, texto, efectos y audio con fotogramas clave, curvas de easing y el editor de curvas.',
  category: 'Herramientas Creativas',
  related: ['animate', 'properties', 'effects-color'],
  sections: [
    {
      title: 'Abrir el editor de fotogramas clave',
      blocks: [
        {
          kind: 'list',
          items: [
            'Selecciona un elemento y abre el editor de fotogramas clave desde el botón de la barra lateral o con `Ctrl+Mayús+A`.',
            'Se abre en una vista **Dividida**: el dopesheet en la parte superior para la temporización y el gráfico de valores debajo.',
            'Cambia de vista con **Gráfico** para curvas de valor y easing, o **Hoja** para mover cada fotograma clave en una sola cuadrícula — las teclas `1` y `2` saltan entre ellas.',
          ],
        },
      ],
    },
    {
      title: 'Añadir un fotograma clave',
      blocks: [
        {
          kind: 'steps',
          items: [
            'Desplaza el cabezal de reproducción al fotograma que deseas clave.',
            'Escribe un valor junto a un parámetro y pulsa `Intro`, o haz clic en el **diamante** de la fila del parámetro.',
            'Desplázate a otro fotograma y establece un segundo valor para crear movimiento entre ellos.',
            'O activa la **auto-clave** (el icono de temporizador) para capturar automáticamente cada cambio de valor mientras trabajas.',
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'No puedes añadir un fotograma clave dentro de una región de transición, ni con el cabezal de reproducción fuera de los límites del clip — el diamante está desactivado allí.',
        },
      ],
    },
    {
      title: 'Qué puedes animar',
      blocks: [
        {
          kind: 'table',
          headers: ['Tipo de clip', 'Valores animables'],
          rows: [
            ['Todos los clips visuales', 'Posición X/Y, ancho, alto, rotación, opacidad, radio de esquina'],
            ['Vídeo', 'Añade punto de anclaje, los cuatro bordes de recorte, suavidad de recorte y volumen'],
            [
              'Texto',
              'Añade escala preestablecida, tamaño de fuente, interlineado, relleno, radio de fondo, sombra y trazo',
            ],
            ['Audio', 'Volumen en decibelios'],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'Los efectos también aportan sus propios parámetros animables — busca el conmutador de fotograma clave junto a cada deslizador.',
        },
      ],
    },
    {
      title: 'Dar forma al movimiento',
      blocks: [
        {
          kind: 'list',
          items: [
            'Haz clic en el **conector** entre dos fotogramas clave para abrir el **editor de easing**.',
            'Explora las curvas preestablecidas en dos pestañas — **Easing Cúbico** y **Resorte**. Filtra los preajustes de easing por dirección (**Todo / Entrada / Salida / Entrada-Salida**); cada mosaico muestra una vista previa animada al pasar el ratón, y **Mantener** congela el valor hasta el siguiente fotograma clave.',
            'Cambia a **Editar** para dar forma a la curva directamente: arrastra las dos asas de control en el gráfico, o escribe valores exactos. Los resortes exponen **tensión**, **fricción** y **masa** y previsualizan su rebote real.',
            'Siente la temporización en **Posición**, **Escala**, **Rotación** u **Opacidad** en la vista previa en vivo, y **Pausa** el bucle.',
            'Guarda una curva ajustada como **preajuste personalizado** con **Guardar como**, **Actualízala** más tarde, o **Restablece** al preajuste del que proviene. Los preajustes personalizados se almacenan en tu dispositivo y aparecen en todos los proyectos.',
            'Copia, corta, pega y elimina fotogramas clave, selecciona grupos con marquesina, arrastra para re-temporizar, y `Alt`-arrastra para duplicar.',
          ],
        },
      ],
    },
    {
      title: 'Trabajar en el gráfico de valores',
      blocks: [
        {
          kind: 'list',
          items: [
            'Elige una propiedad del menú desplegable para editar su curva, o déjalo en "todas" para ver las demás como curvas superpuestas atenuadas. El recuento de fotogramas clave para la propiedad activa se muestra junto a ella.',
            'El gráfico dibuja una **cuadrícula** etiquetada — fotogramas o segundos en el eje horizontal, valores en el vertical — para que puedas leer las posiciones de un vistazo.',
            'Encuadra la curva con **Ajustar al contenido**, o ajusta la vista con **Ampliar** / **Reducir** y la rueda del ratón. **Restablecer vista** vuelve al ajuste predeterminado.',
            'Recorre los fotogramas clave con las flechas **anterior** / **siguiente**, luego escribe un fotograma exacto (**F**) y valor (**V**) para el fotograma clave seleccionado.',
            'Mientras arrastras un fotograma clave, mantén **Mayús** para bloquearlo en un eje, o **Alt** para ajustarlo finamente.',
          ],
        },
      ],
    },
    {
      title: 'Movimiento generado',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Algunos clips llevan movimiento generado como deriva, respiración, vibración o pulso de audio, evaluado mientras se reproducen. Un clip con movimiento generado muestra una acción **Hornear movimiento**.',
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'Hornea el movimiento para convertirlo en fotogramas clave editables que puedas ajustar manualmente.',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page